/* System defined core library */
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined enums/constants */
import { ComponentType, Fee } from '../../../shared/enums/scheduling.enum';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { Session } from '../../../shared/models/scheduling/session.model';

/* User defined library/components */
import { DemographicsService } from '../../../shared/services/demographics.service';
import { DynamicComponentService } from '../../../shared/services/dynamic.component.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { TimerService } from '../../../shared/services/timer.service';
import { MultidayTestCenterSelectorComponent } from '../../shared/components/multiday-testcenter-selector/multiday-testcenter-selector.component';


@Component({
  selector: 'app-multiday-testcenter',
  templateUrl: './multiday-testcenter.component.html',
  styleUrls: ['./multiday-testcenter.component.scss'],
  providers: [DynamicComponentService]
})
export class MultidayTestCenterComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }

  @ViewChild('refreshavailabilitybtn') refreshBtn: ElementRef;
  @ViewChild(MultidayTestCenterSelectorComponent) multidayTestCenterSelectorComponentInstance: MultidayTestCenterSelectorComponent;

  /* User defined private/public variables  */
  public selectedAppointments: AppointmentDetail[] = [];
  public region: any;
  public client: Client;
  public program: Program;
  public candidate: Candidate;
  public canNavigateNext: boolean = false;
  public holds: Hold[];
  public consecutiveDays: boolean;
  public hasConflictErrorOccured: boolean;

  /* Service subscriptions */
  selectedAppointments$: Subscription;
  hold$: Subscription;
  region$: Subscription;
  selectedSite$: Subscription;
  client$: Subscription;
  program$: Subscription;
  translateLanguage$: Subscription;
  candidate$: Subscription;
  holds$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private demographicsService: DemographicsService,
    private dynamicComponentService: DynamicComponentService,
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private cdRef: ChangeDetectorRef
  ) {
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.region$ = selectedExamDetailService.region.subscribe(region => {
      this.region = region;
    });

    this.program$ = this.selectedExamDetailService.program.subscribe(program => {
      this.program = program;
    });

    this.client$ = this.selectedExamDetailService.client.subscribe(client => {
      this.client = client;
    });

    this.candidate$ = this.selectedExamDetailService.candidate.subscribe(candidate => {
      this.candidate = candidate;
    });

    this.holds$ = this.selectedExamDetailService.holds.subscribe(holds => {
      this.holds = holds;
    });
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.MultidayTestCenterComponent)) return;
    this.selectedAppointments.forEach(
      appt => {
        appt.exam.hasPayment = true;
      });
    this.consecutiveDays = this.selectedExamDetailService.consecutiveDays;
  }

  setSelectedSessions(sessions: Session[]) {
    this.selectedAppointments[0].exam.sessions = sessions;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    this.hasConflictErrorOccured = false;
  }

  /* Move back a page. */
  goToPrevPage() {
    this.clearDataOnGoingBack();
    this.routingService.scheduleBwdNavigation(ComponentType.MultidayTestCenterComponent);
  }

  /* Use to clear details on navigating backward */
  clearDataOnGoingBack() {
    this.selectedExamDetailService.setSkipSize(0);
    this.selectedExamDetailService.setSiteSlots(null);
    this.selectedExamDetailService.setSessionGroups([]);
    this.selectedExamDetailService.setSessionAvailabilities([]);
    this.selectedExamDetailService.setSiteSlot(null);
    this.selectedExamDetailService.setHolds([]);
    this.selectedAppointments[0].exam.sessions.forEach((s) => {
      s.slot = null;
      s.site = null;
    });
    this.selectedExamDetailService.SetAppointmentDetail(this.selectedAppointments);

    this.timerService.clearClock();
  }

  canNavigateFwd($event: boolean) {
    this.canNavigateNext = $event;
  }

  // TODO - move these methods to service - KJC
  goToNextPage() {
    if (!this.holds || (this.holds && !this.holds.length)) {
      this.createHolds();
    } else if (this.holds.length) {
      this.moveToNextPage();
    }
    this.getPaymentDetails();
  }

  refreshSlotAvailability(): void {
    this.multidayTestCenterSelectorComponentInstance.submitSearch();
  }

  // TODO - move these methods to service - KJC
  /* Create Holds for all session slots */
  createHolds() {
    let slots = this.selectedAppointments[0].exam.sessions.map(s => s.slot);

    this.schedulingApiService.holdMultipleSlots(slots).subscribe((response: any) => {
      if (response && response.length) {
        this.selectedAppointments[0].exam.sessions.forEach((session, index) => {
          session.hold.id = response[index].id;
          // set the hold object and can be removed after the S/R flow does not look at hold object and looks at holds objects instead
          if (index === 0) this.selectedExamDetailService.setHold(session.hold);
        });
        const holds = this.selectedAppointments[0].exam.sessions.map(s => s.hold);
        this.selectedExamDetailService.setHolds(holds);
        this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
        this.moveToNextPage();
      }
      else {
        if (response == -2) {
          this.hasConflictErrorOccured = true;
          this.canNavigateNext = false;
          this.cdRef.detectChanges();
          this.refreshBtn.nativeElement.focus();
        }
      }
    })
  }

  // TODO - move these methods to service - KJC
  moveToNextPage() {
    const selectedSite = this.selectedAppointments[0].exam.sessions[0].site;
    // retrieving consent for an exam
    this.orchestrationApiService.getFlows(
      this.client.code,
      this.program.code,
      this.selectedAppointments[0].exam.code,
      selectedSite.location.address.country
    ).subscribe((resp) => {
      this.setSettingsFromFlows(resp);
      this.routingService.scheduleFwdNavigation(ComponentType.MultidayTestCenterComponent);
    });
  }

  // TODO - move these methods to service - KJC
  getPaymentDetails(): void {
    let totalAmount: number = 0;
    let currencyCode: string = '';
    let isPaymentRequired: boolean;

    // NO B2B, only single exam
    // Just get first session site country as all sessions would be at same site
    let country = this.selectedAppointments[0].exam.sessions[0].site.location.address.country;

    this.selectedAppointments.forEach((appointmentDetail) => {
      appointmentDetail.payments = [];
      this.schedulingApiService.getPaymentFeeDetail(appointmentDetail.exam.id, country).subscribe((paymentFee) => {
        paymentFee.forEach(payment => {
          if (payment.type == Fee.InitialScheduleFee) {
            let tmpPayment: Payment = new Payment(
              payment.amount,
              payment.currency,
              Fee.InitialScheduleFee
            );
            totalAmount += payment.amount;
            currencyCode = payment.currency;
            appointmentDetail.payments.push(tmpPayment);
            this.selectedExamDetailService.scheduleFee = {
              totalAmount: totalAmount,
              currency: currencyCode
            };

            isPaymentRequired = appointmentDetail.exam.hasPayment && totalAmount > 0;
            this.selectedExamDetailService.isPaymentRequired = isPaymentRequired;

            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

            if (!appointmentDetail.exam.hasPayment) {
              this.clearPaymentDetails(appointmentDetail.exam.id);
            }
          }
        });

      });
    });
  }

  // TODO - move these methods to service - KJC
  clearPaymentDetails(examId?: any) {
    examId = examId || this.selectedAppointments[0].exam.id;
    let totalAmount = 0;
    this.selectedAppointments.forEach((appointmentDetail) => {
      if (appointmentDetail.exam.id === examId) {
        appointmentDetail.payments.forEach((payment) => {
          if (payment) {
            payment.amount = 0;
          } else {
            let tmpPayment: Payment = new Payment(0);
            appointmentDetail.payments.push(tmpPayment);
          }
        });
      }
    });

    this.selectedAppointments.forEach(appt => totalAmount += appt.payments.length ? appt.payments[0].amount : 0);
    this.selectedExamDetailService.scheduleFee = {
      totalAmount: totalAmount,
    };

    this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  // TODO - move these methods to service - KJC
  setSettingsFromFlows(response: any, examId?: string) {
    const notificationResp = response.filter(function (el: any) {
      return el.name === 'Verification';
    });

    this.selectedExamDetailService.showTextReminder = notificationResp[0].data &&
      notificationResp[0].data.length &&
      notificationResp[0].data[0].parameters.smsText === 'True' ? true : false;

    const paymentResp = response.filter(function (el: any) {
      return el.name == 'Payment';
    });

    examId = examId || this.selectedAppointments[0].exam.id;

    this.selectedAppointments.forEach(appt => {
      if (appt.exam.id === examId) {
        appt.exam.hasPayment = paymentResp.length && paymentResp[0].settings.schedule === 'True';
      }
    });
    this.selectedExamDetailService.canSkipProfile = this.demographicsService.canSkipDemographicPage(response, this.candidate);

    let biometricResp = response.filter(function (el: any) {
      return el.name == 'Biometric Consent';
    });

    let isBiometricConsentRequiredSchedule = (biometricResp.length && biometricResp[0].settings && biometricResp[0].settings.consent === 'True');
    this.selectedExamDetailService.isBiometricConsentRequiredSchedule = isBiometricConsentRequiredSchedule;

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

    if (this.selectedExamDetailService.canSkipProfile) {
      this.dynamicComponentService.setCandidateProfileforSkip(response);
    }

    this.selectedAppointments.forEach(appt => {
      if (!appt.exam.hasPayment) {
        this.clearPaymentDetails(appt.exam.id);
      }
    });
  }
}
